import { EpisodeCard as EpisodeCardType, Referrer } from '@/types';

import {
  EpisodeCardJumbo,
  EpisodeCardLarge,
  EpisodeCardMedium,
  EpisodeCardSmall,
} from './components';
import { EpisodeCardVariants } from './constants';
import { EpisodeCardSkeleton } from './EpisodeCardSkeleton';
import { useEpisodeBanner } from './useEpisodeBanner';
import { useEpisodeCard } from './useEpisodeCard';

interface EpisodeCardProps {
  episode?: EpisodeCardType;
  isLoading?: boolean;
  onLinkClick?: () => void;
  referrer?: Referrer;
  variant?: (typeof EpisodeCardVariants)[keyof typeof EpisodeCardVariants];
  stretch?: boolean;
}

type EpisodeCardContainerProps = EpisodeCardProps &
  Required<Pick<EpisodeCardProps, 'episode'>>;

export const EpisodeCard = ({
  episode,
  variant = EpisodeCardVariants.MEDIUM,
  isLoading = false,
  onLinkClick,
  referrer = {},
  stretch = false,
}: EpisodeCardProps) => {
  if (!episode || isLoading)
    return (
      <EpisodeCardSkeleton variant={variant} stretch={stretch} includeText />
    );

  return (
    <EpisodeCardContainer
      episode={episode}
      variant={variant}
      isLoading={isLoading}
      onLinkClick={onLinkClick}
      referrer={referrer}
      stretch={stretch}
    />
  );
};

const EpisodeCardContainer = ({
  episode,
  variant = EpisodeCardVariants.MEDIUM,
  isLoading = false,
  onLinkClick,
  referrer = {},
  stretch = false,
}: EpisodeCardContainerProps) => {
  const location = {
    ...referrer,
    variants: { ...referrer.variants, episodeCardVariant: variant },
  };

  const episodeCardProps = useEpisodeCard({
    episode,
    referrer: location,
  });
  const episodeBannerProps = useEpisodeBanner({
    episode,
    referrer: location,
  });

  const commonProps = {
    stretch,
    isLoading,
    onLinkClick,
  };

  return {
    [EpisodeCardVariants.SMALL]: (
      <EpisodeCardSmall {...episodeCardProps} {...commonProps} />
    ),
    [EpisodeCardVariants.MEDIUM]: (
      <EpisodeCardMedium {...episodeCardProps} {...commonProps} />
    ),
    [EpisodeCardVariants.LARGE]: (
      <EpisodeCardLarge {...episodeBannerProps} {...commonProps} />
    ),
    [EpisodeCardVariants.JUMBO]: (
      <EpisodeCardJumbo {...episodeBannerProps} {...commonProps} />
    ),
  }[variant];
};
